import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSubscriptionStatus, SUB_STATUS } from '../../hooks/useSubscriptionStatus';
import SubscriptionBanner from 'components/SubscriptionBanner';

import PrimaryNavBar from 'components/ui/PrimaryNavBar';
import Icon from 'components/AppIcon';
import { SkeletonCard, SkeletonRow, SkeletonChart } from '../../components/skeletons';

import LiveBenchmarkPanel from './components/LiveBenchmarkPanel';
import EurUsdFeedPanel from './components/EurUsdFeedPanel';
import MarketHistoryTable from './components/MarketHistoryTable';
import BenchmarkTrendCharts from './components/BenchmarkTrendCharts';

const SECTIONS = [
  { id: 'benchmarks', label: 'Benchmarks', icon: 'TrendingUp' },
  { id: 'eurusd',     label: 'EUR/USD',    icon: 'DollarSign' },
  { id: 'history',   label: 'Historial',  icon: 'Table' },
  { id: 'charts',    label: 'Tendencias', icon: 'BarChart2' },
];

export default function MarketData() {
  const { user } = useAuth();
  const [ticks, setTicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [activeSection, setActiveSection] = useState('benchmarks');
  const [error, setError] = useState(null);
  const manualEntryRef = React.useRef(null);

  // ─── Load all market ticks ─────────────────────────────────────────────────
  const loadTicks = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error: err } = await supabase
        ?.from('market_ticks')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('ts', { ascending: false })
        ?.limit(2000);

      if (err) throw err;
      setTicks(data || []);
      setLastSync(new Date());
      setError(null);
    } catch (err) {
      console.error('Error loading market ticks:', err);
      setError(err?.message || 'Error al cargar datos de mercado');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadTicks();
  }, [loadTicks]);

  // Real-time subscription with error recovery
  const { status: subStatus, subWarningBanner, retryNow } = useSubscriptionStatus({
    channelName: `market_data_page_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'market_ticks',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: (payload) => {
      if (payload?.eventType === 'INSERT') {
        setTicks(prev => [payload?.new, ...prev]);
        setLastSync(new Date());
      } else if (payload?.eventType === 'UPDATE') {
        setTicks(prev => prev?.map(t => t?.id === payload?.new?.id ? payload?.new : t));
      } else if (payload?.eventType === 'DELETE') {
        setTicks(prev => prev?.filter(t => t?.id !== payload?.old?.id));
      }
    },
    onReconnect: loadTicks,
  });

  // ─── EUR/USD rate ──────────────────────────────────────────────────────────
  const eurUsd = useMemo(() => {
    const tick = ticks
      ?.filter(t => t?.symbol === 'EUR_USD')
      ?.sort((a, b) => new Date(b.ts) - new Date(a.ts))?.[0];
    return tick ? parseFloat(tick?.value) : null;
  }, [ticks]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleEditRegional = useCallback(async (symbol, value) => {
    if (!user?.id) return;
    await supabase
      ?.from('market_ticks')
      ?.insert({ user_id: user?.id, symbol, value, currency: 'EUR', source: 'ÍNDICE_INTERNO', ts: new Date()?.toISOString() });
  }, [user?.id]);

  const handleSaveEurUsdOverride = useCallback(async (value) => {
    if (!user?.id) return;
    await supabase
      ?.from('market_ticks')
      ?.insert({ user_id: user?.id, symbol: 'EUR_USD', value, currency: 'USD', source: 'MANUAL_OVERRIDE', ts: new Date()?.toISOString() });
  }, [user?.id]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavBar />
      <main className="pt-[60px]">
        {/* Page Header */}
        <div className="border-b border-border bg-card">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-md flex items-center justify-center"
                  style={{ background: 'rgba(234,88,12,0.1)' }}>
                  <Icon name="TrendingUp" size={18} color="var(--color-accent)" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-foreground font-heading">Datos de Mercado</h1>
                  <p className="text-xs text-muted-foreground font-caption">
                    Benchmarks LME · Índices regionales · Historial de ticks
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {lastSync && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-data">
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      subStatus === SUB_STATUS?.CONNECTED ? 'bg-success animate-pulse' :
                      subStatus === SUB_STATUS?.FAILED ? 'bg-red-500' : 'bg-amber-400 animate-pulse'
                    }`} />
                    {lastSync?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </div>
                )}
                <button
                  onClick={loadTicks}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-md bg-background hover:bg-muted transition-all disabled:opacity-50"
                >
                  <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={13} color="currentColor"
                    className={loading ? 'animate-spin' : ''} />
                  Actualizar
                </button>
              </div>
            </div>

            {/* Section tabs */}
            <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
              {SECTIONS?.map(s => (
                <button
                  key={s?.id}
                  onClick={() => setActiveSection(s?.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-caption whitespace-nowrap transition-all ${
                    activeSection === s?.id
                      ? 'bg-accent text-white' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={s?.icon} size={13} color="currentColor" />
                  {s?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 py-6">
          <SubscriptionBanner status={subStatus} subWarningBanner={subWarningBanner} retryNow={retryNow} />

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-error/10 border border-error/20 text-error text-sm">
              <Icon name="AlertCircle" size={16} color="currentColor" />
              {error}
            </div>
          )}

          {loading ? (
            <div className="space-y-6">
              {/* LME benchmark cards: 5 + regional: 4 */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                {Array.from({ length: 5 })?.map((_, i) => (
                  <SkeletonCard key={i} height="110px" />
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 4 })?.map((_, i) => (
                  <SkeletonCard key={i} height="90px" />
                ))}
              </div>
              {/* Sparkline + trend charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SkeletonChart height="180px" />
                <SkeletonChart height="180px" />
              </div>
              {/* History table */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-border">
                  <SkeletonCard height="24px" width="200px" rounded="rounded-md" />
                </div>
                <table className="w-full">
                  <tbody className="divide-y divide-border">
                    {Array.from({ length: 10 })?.map((_, i) => (
                      <SkeletonRow key={i} cols={5} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {activeSection === 'benchmarks' && (
                <div data-tour="market-benchmark-cards">
                  <LiveBenchmarkPanel
                    ticks={ticks}
                    eurUsd={eurUsd}
                    onEditRegional={handleEditRegional}
                  />
                </div>
              )}

              {activeSection === 'eurusd' && (
                <div data-tour="market-eurusd">
                  <EurUsdFeedPanel
                    ticks={ticks}
                    onSaveOverride={handleSaveEurUsdOverride}
                  />
                </div>
              )}

              {activeSection === 'history' && (
                <MarketHistoryTable
                  ticks={ticks}
                />
              )}

              {activeSection === 'charts' && (
                <BenchmarkTrendCharts ticks={ticks} />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
